import { Autocomplete, TextField, Grid, Box, Chip, createFilterOptions, Checkbox, FormControlLabel, FormHelperText, Skeleton } from '@mui/material'
import React, { useEffect, useReducer, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { validateForm } from '../shared/form-validation'
import { LoadNewPlans, LoadOrganizations, LoadPlans } from '../../actions'
import { NewOrganization, OrganizationModel } from '../../models/organization.model'
import { useSelector } from 'react-redux'
import * as selector from '../../selectors/selectors';
import { Feature, FeatureType, mapToNewFeature, NewPlanType, Plan, } from '../../models/plans.model'
import ButtonComponent from '../shared/button/button.component'
import { FeatureService } from '../../services/FeatureService'
import { mapToFeature } from '../../models/features.model'
import { PlansService } from '../../services/plans.service'
import { setBackHandler, setHeaderTitle } from '../../helpers/header.helper'
import { buttonLabels, ToasterTexts } from '../shared/constants'
import setToast from '../../helpers/toaster.helper'
import { ToasterType } from '../shared/toaster/toaster.model'


interface State {
    name: string
    footNote: string
    monthlyPrice: string
    annualPrice: string
    country: { ISO: string; Name: string } | null
    privacy: boolean
    selectedOrganizations: string[]
    PlanType: NewPlanType
    features: Feature[]
    selectAll?: boolean
    errors: {
        [key: string]: string
    }
    isEdit: boolean;
    buttonLable: string;
}

type Action =
    | { type: 'SET_FIELD'; field: keyof State; value: any }
    | { type: 'SET_ERROR'; field: keyof State; error: string }
    | { type: 'RESET_ERRORS' }

const countries = [
    { ISO: "US", Name: "United States" },
    { ISO: "GB", Name: "United Kingdom" },
    { ISO: "CA", Name: "Canada" },
]

const Privacy = [
    { Name: "False", Value: false },
    { Name: "True", Value: true }
]

const newPlanType = [
    { Name: "Plan", Value: NewPlanType.LITE },
    { Name: "Addon", Value: NewPlanType.PREMIUM },
]

const initialState: State = {
    name: '',
    footNote: '',
    monthlyPrice: '',
    annualPrice: '',
    country: countries[0] || null,
    privacy: false,
    selectedOrganizations: [],
    PlanType: NewPlanType.LITE,
    features: [],
    selectAll: false,
    errors: {},
    isEdit: false,
    buttonLable: "Add Plan"
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value }
        case 'SET_ERROR':
            return { ...state, errors: { ...state.errors, [action.field]: action.error } }
        case 'RESET_ERRORS':
            return { ...state, errors: {} }
        default:
            return state
    }
}

interface CreateEditPlanProps {
    initialState?: Partial<State>
}

function CreateEditPlan({ initialState: initialStateProp }: CreateEditPlanProps) {
    const params = useParams()
    const navigate = useNavigate()
    const mergedInitialState = { ...initialState, ...initialStateProp }
    const [state, localDispatch] = useReducer(reducer, mergedInitialState)
    const NewOrganizations: NewOrganization[] = useSelector(selector.newOrganizations)
    const Plans: Plan[] = useSelector(selector.newPlans)
    const loadedNewPlans: boolean = useSelector(selector.loadedNewPlans)

    const [isLoading, setIsLoading] = useState<boolean>(false)

    const Autofilter = createFilterOptions();

    const handleFieldChange = (field: keyof State) => (e: React.ChangeEvent<HTMLInputElement>) => {
        localDispatch({ type: 'SET_FIELD', field, value: e.target.value })
        validateForm({ [field]: e.target.value }, (error: any) => {
            if (error) {
                const fieldError = error[field]
                localDispatch({ type: 'SET_ERROR', field, error: fieldError })
            } else {
                localDispatch({ type: 'SET_ERROR', field, error: '' })
            }
        })
    }

    const handleCountryChange = (_: any, newValue: any) => {
        localDispatch({ type: 'SET_FIELD', field: 'country', value: newValue })
    }

    const handlePrivacyChange = (_: any, newValue: any) => {
        localDispatch({ type: 'SET_FIELD', field: 'privacy', value: newValue?.Value ?? false })
    }

    const getOrganizations = () => {
        return NewOrganizations.filter(x => !x.IsDeleted)
    }
    const handleCheckBox = (_: any, value: OrganizationModel[]) => {
        const selectedIds = value.map(org => org.ID);
        localDispatch({ type: 'SET_FIELD', field: 'selectedOrganizations', value: selectedIds });
        if (value.length < 1) {
            localDispatch({ type: "SET_ERROR", field: "selectedOrganizations", error: "Please Select at least one organization" })
        }
    }

    const handlePlanTypeChange = (_: any, newValue: any) => {
        localDispatch({ type: 'SET_FIELD', field: 'PlanType', value: newValue?.Value ?? NewPlanType.LITE })
    }

    const handleSelectfeature = (id: string, isChecked: boolean) => {
        const updatedFeatures = state.features.map(feature => {
            if (feature.Id === id) {
                return { ...feature, SelectedFeature: isChecked, Validity: feature.Code === "CPMF" ? isChecked : false, Value: feature.Code === "CPMF" ? "-1" : "", SelectedFeatureError: isChecked ? feature.Code === "CPMF" ? "" : "Please select Unlimited or enter a value." : "" }
            }
            return feature;
        });
        localDispatch({ type: 'SET_FIELD', field: 'features', value: updatedFeatures });
    }

    const handleUnlimited = (id: string, isChecked: boolean) => {
        const updatedFeatures = state.features.map(feature => {
            if (feature.Id === id) {
                return { ...feature, Validity: isChecked, Value: isChecked ? "-1" : "", SelectedFeatureError: "" }
            }
            return feature;
        });
        localDispatch({ type: 'SET_FIELD', field: 'features', value: updatedFeatures });
    }

    const handleFeatureValue = (id: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        const updatedFeatures = state.features.map(feature => {
            if (feature.Id === id) {
                return { ...feature, Value: value, SelectedFeatureError: "" }
            }
            return feature;
        });
        localDispatch({ type: 'SET_FIELD', field: 'features', value: updatedFeatures });
    }
    const handleKeyPress = (e) => {
        if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
            e.preventDefault();
        }
    }

    const handleSelectAll = (isChecked: boolean) => {
        const selectedFeatures = state.features.map(x => x.FeatureType === 1 ? { ...x, SelectedFeature: isChecked } : x)

        localDispatch({ type: 'SET_FIELD', field: 'features', value: selectedFeatures });
        localDispatch({ type: 'SET_FIELD', field: 'selectAll', value: isChecked });
    }

    const handleAdditionalFeature = (id: string, isChecked: boolean) => {
        const updatedFeatures = state.features.map(feature => {
            if (feature.Id === id) {
                return { ...feature, SelectedFeature: isChecked }
            }
            return feature;
        });
        localDispatch({ type: 'SET_FIELD', field: 'features', value: updatedFeatures });
        const selectAll = updatedFeatures.filter(x => x.FeatureType === FeatureType.ADDITIONAL && !x.SelectedFeature)
        localDispatch({ type: 'SET_FIELD', field: 'selectAll', value: !!selectAll?.length ? false : true });

    }

    useEffect(() => {
        if (Plans?.length < 1) {
            LoadNewPlans(localDispatch)
        }
        if (NewOrganizations?.length < 1) {
            LoadOrganizations(localDispatch)
        }
        setHeaderTitle("Add Plan")
        setBackHandler(() => { navigate(-1) })
    }, [])

    const optionRenderer = (defaultprops, option: any, { selected }: any) => {
        const selectAllProps =
            state.selectedOrganizations.length > 0 && option.value === -1
                ? { checked: (getOrganizations().length === state.selectedOrganizations.length) }
                : {};
        return (
            <li {...defaultprops}>
                <Checkbox
                    name="checkbox"
                    className="mr-1"
                    classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                    indeterminate={option.value === -1 && getOrganizations().length - 1 === state.selectedOrganizations.length}
                    checked={selected}
                    {...selectAllProps}
                />
                <div className={`my-auto t-align-left`}> {option?.Name} </div>
            </li >
        )
    }

    const handleCreatePlan = () => {
        localDispatch({ type: 'RESET_ERRORS' });

        const errors: { [key: string]: string } = {};
        if (!state.name) errors.name = "Please enter a plan name";
        if (!state.footNote) errors.footNote = "Please enter a footnote";
        if (!state.country) errors.country = "Please select a country";
        if (state.PlanType === NewPlanType.LITE && !state.monthlyPrice) errors.monthlyPrice = "Please enter a monthly price";
        if (state.PlanType === NewPlanType.PREMIUM && !state.annualPrice) errors.annualPrice = "Please enter an annual price";
        if (state.features.filter(x => x.SelectedFeature).length < 1) errors.selectedFeatures = "Please select at least one feature";
        if (state.privacy && state.selectedOrganizations.length < 1) errors.selectedOrganizations = "Please select at least one organization";

        Object.keys(errors).forEach(key => {
            localDispatch({ type: 'SET_ERROR', field: key as keyof State, error: errors[key] });
        });

        if (Object.keys(errors).length > 0) return;

        const planData = {
            id: state.isEdit ? params?.PlanID : "",
            name: state.name,
            fNotes: state.footNote,
            price: Number(state.monthlyPrice),
            yPrice: Number(state.annualPrice),
            country: state.country?.ISO || '',
            color: "#000",
            planType: state.PlanType,
            isDeleted: true,
            features: state.features.filter(x => x.SelectedFeature).map(x => ({
                id: x.Id,
                value: !!x.Value ? String(x.Value) : "-1",
                // validity: x.Validity,
                featureType: x.FeatureType,
                code: x.Code,
                label: x.Label,
                displayOrder: x.DisplayOrder,
            }))
        };

        // Call the API to create the plan
        if (state.isEdit) {
            setIsLoading(true)
            PlansService.updateNewPlans(planData).then((res) => {
                setIsLoading(false)
                setToast(ToasterTexts.TIER_EDIT_SUCCESS.replace('<Name>', state.name), ToasterType.SUCCESS)
                navigate(-1);
            }).catch((err) => {
                localDispatch({ type: 'SET_ERROR', field: 'name', error: err.response.data.message });
                setIsLoading(false);
                setToast(err.response.data.message, ToasterType.DANGER)
            })
        }
        else {
            setIsLoading(true)
            PlansService.createNewPlan(planData)
                .then((res) => {
                    setIsLoading(false)
                    setToast(ToasterTexts.TIER_ADD_SUCCESS.replace('<Name>', state.name), ToasterType.SUCCESS)
                    navigate(-1);
                })
                .catch((error) => {
                    setIsLoading(false)
                    localDispatch({ type: 'SET_ERROR', field: 'name', error: error.response.data.message });
                    setToast(error.response.data.message, ToasterType.DANGER)
                });
        }
    }

    useEffect(() => {
        if (loadedNewPlans) {
            FeatureService.GetAllNewFeatures().then((response) => {
                const planID = params?.PlanID
                let allfeatureData = response.data.map(mapToNewFeature)
                allfeatureData = allfeatureData.map(item => ({ ...item, SelectedFeature: false, Validity: false, Value: "", SelectedFeatureError: "" }))

                if (planID === "addPlan") {
                    localDispatch({ type: 'SET_FIELD', field: 'features', value: allfeatureData });
                    setHeaderTitle("Add Plan")
                }
                else {
                    setHeaderTitle(planID.includes("duplicate") ? "Duplicate Plan" : "Edit Plan")
                    setBackHandler(() => { navigate(-1) })
                    let filterPlan: Plan = Plans.find(x => x.Id === planID.replace('duplicate', ''))
                    let filterdata = allfeatureData?.map((item: Feature) => {
                        let plan = filterPlan?.Features?.find(x => x.Id === item.Id)
                        if (plan) {
                            item.SelectedFeature = true
                            item.Validity = plan.Validity
                            item.Value = plan.Value
                            return { ...item, ...plan }
                        }
                        return item
                    })
                    const updatedFeatureArray = filterdata?.map(item => {
                        if (item?.SelectedFeature) {
                            item.Validity = item.Value === "-1" ? true : false;
                            item.Value = item.Value !== "-1" ? item?.Value : null;
                        }

                        return item;
                    });
                    let isChecked = updatedFeatureArray.filter(x => x.FeatureType === 1)?.length === updatedFeatureArray.filter(x => x.FeatureType === 1).filter(item => item.SelectedFeature === true)?.length

                    Object.entries({
                        name: filterPlan.Name,
                        footNote: filterPlan.FootNote,
                        country: countries.find(x => x.ISO === filterPlan.Country),
                        monthlyPrice: String(filterPlan.Price),
                        annualPrice: String(filterPlan.YPrice),
                        isPrivate: filterPlan.IsPrivate,
                        selectedOrganizations: filterPlan.SelectedOrganizations,
                        buttonLable: planID.includes("duplicate") ? buttonLabels.DUPLICATE_TIER : buttonLabels.UPDATE_TIER,
                        isEdit: planID.includes("duplicate") ? false : true,
                        PlanType: filterPlan.PlanType,
                        selectAll: isChecked,
                        features: allfeatureData
                    }).forEach(([field, value]) => {
                        localDispatch({ type: 'SET_FIELD', field: field as keyof State, value });
                    });
                }
            }).catch((err) => {

            })
        }

        return setHeaderTitle()

    }, [params?.PlanID, loadedNewPlans])

    return (
        <Box sx={{ flexGrow: 1 }} className="p-2 h-p-90 o-y-scroll">
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Plan Name"
                        value={state.name}
                        onChange={handleFieldChange('name')}
                        error={!!state.errors.name}
                        helperText={state.errors.name}
                        margin="normal"
                        size='small'
                        FormHelperTextProps={{ className: "ml-auto mr-0" }}
                        disabled={state.isEdit}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="FootNote"
                        value={state.footNote}
                        onChange={handleFieldChange('footNote')}
                        error={!!state.errors.footNote}
                        helperText={state.errors.footNote}
                        margin="normal"
                        size='small'
                        FormHelperTextProps={{ className: "ml-auto mr-0" }}
                    />
                </Grid>
                <Grid item xs={12} md={6} className='pt-0'>
                    <Autocomplete
                        size="small"
                        className="w-fill"
                        classes={{
                            input: 'h2 color-text-field-font',
                            paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow'
                        }}
                        options={countries}
                        getOptionLabel={(option: any) => option.Name}
                        onChange={handleCountryChange}
                        value={state.country}
                        renderOption={(defaultprops, option: any) => (
                            <li {...defaultprops}>
                                <span className="c-pointer h2 color-primary-dark p-2 d-flex justify-between w-p-90">
                                    {option.Name}
                                </span>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                name="country"
                                required
                                variant="outlined"
                                label="Select Country"
                                placeholder="Select Country"
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: 'new-password'
                                }}
                                error={!!state.errors.country}
                                helperText={state.errors.country}
                                margin="normal"
                                FormHelperTextProps={{ className: "ml-auto mr-0" }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6} className='pt-0'>
                    <Autocomplete
                        disabled
                        size="small"
                        className="w-fill"
                        classes={{
                            input: 'h2 color-text-field-font',
                            paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow'
                        }}
                        options={newPlanType}
                        getOptionLabel={(option: any) => option.Name}
                        onChange={handlePlanTypeChange}
                        value={newPlanType.find(x => x.Value === state.PlanType) || newPlanType[0]}
                        renderOption={(defaultprops, option: any) => (
                            <li {...defaultprops}>
                                <span className="c-pointer h2 color-primary-dark p-2 d-flex justify-between w-p-90">
                                    {option.Name}
                                </span>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                name="PlanType"
                                required
                                variant="outlined"
                                label="Plan Type"
                                placeholder="Public"
                                error={!!state.errors.PlanType}
                                helperText={state.errors.PlanType}
                                margin="normal"
                                FormHelperTextProps={{ className: "ml-auto mr-0" }}
                            />
                        )}
                        disableClearable={true}
                    />
                </Grid>
                <Grid item xs={12} md={6} className='pt-0'>
                    <TextField
                        fullWidth
                        label="Monthly price"
                        value={state.monthlyPrice}
                        onChange={handleFieldChange('monthlyPrice')}
                        error={!!state.errors.monthlyPrice}
                        helperText={state.errors.monthlyPrice}
                        margin="normal"
                        size='small'
                        FormHelperTextProps={{ className: "ml-auto mr-0" }}
                    />
                </Grid>
                <Grid item xs={12} md={6} className='pt-0'>
                    <TextField
                        fullWidth
                        label="Annual Price"
                        value={state.annualPrice}
                        onChange={handleFieldChange('annualPrice')}
                        error={!!state.errors.annualPrice}
                        helperText={state.errors.annualPrice}
                        margin="normal"
                        size='small'
                        FormHelperTextProps={{ className: "ml-auto mr-0" }}
                    />
                </Grid>
            </Grid>
            {/* Main Features Section */}
            <div className='mt-2 mx-1'>
                <label className='h1-regular'>Main Features*</label>
                {
                    state.features.length === 0 ? (
                        // MUI Skeleton Loader while features are loading
                        Array.from({ length: 2 }).map((_, idx) => (
                            <div key={idx} className="">
                                <div className="d-flex justify-between align-center">
                                    <div className="w-p-20">
                                        <Skeleton variant="rectangular" width={120} height={37} sx={{ borderRadius: 2 }} />
                                    </div>
                                    <div className="">
                                        <Skeleton variant="rectangular" width={90} height={37} sx={{ borderRadius: 2 }} />
                                    </div>
                                    <div>
                                        <Skeleton variant="rectangular" width={120} height={37} sx={{ borderRadius: 2 }} />
                                    </div>
                                </div>
                                <Skeleton variant="text" width={180} height={10} className="ml-auto mr-1" />
                            </div>
                        ))
                    ) : (
                        state.features.filter(x => x.FeatureType === 0).map((item) =>
                            <div key={item.Id}>
                                <div className='d-flex justify-between align-center'>
                                    <div className='w-p-20'>
                                        <FormControlLabel
                                            control={<Checkbox
                                                defaultChecked={false}
                                                checked={item.SelectedFeature}
                                                classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                                                onChange={(e) => handleSelectfeature(item.Id, e?.target.checked)}
                                            />}
                                            label={item.Name} />
                                    </div>
                                    <div className='mt-1'>
                                        <FormControlLabel
                                            control={<Checkbox
                                                defaultChecked={false}
                                                checked={item.Validity}
                                                disabled={!item.SelectedFeature || item.Code === "CPMF"}
                                                classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                                                onChange={(e) => handleUnlimited(item.Id, e.target?.checked)}
                                            />} label="Unlimited" />
                                    </div>
                                    <div>
                                        <TextField
                                            type="number"
                                            name="value"
                                            value={item.Value}
                                            disabled={(item.Validity && item.SelectedFeature) || (!item.Validity && !item.SelectedFeature)}
                                            label="Value"
                                            variant="outlined"
                                            onChange={(e) => handleFeatureValue(item.Id, e)}
                                            onKeyPress={(e) => handleKeyPress(e)}
                                            autoComplete='no'
                                            size="small"
                                            required
                                            fullWidth
                                            InputProps={{
                                                classes: { input: `h4-regular color-text-field-font` },
                                                style: {
                                                    backgroundColor: ((item.Validity && item.SelectedFeature) || (!item.Validity && !item.SelectedFeature)) ? 'lightgrey' : 'transparent',
                                                }
                                            }}
                                            className="mx-1  w-fill" />
                                    </div>
                                </div>
                                <FormHelperText className="h5 color-primary-danger t-align-right ml-auto mr-1">
                                    {item.SelectedFeatureError}
                                </FormHelperText>
                            </div>
                        )
                    )
                }
            </div>
            {/* Additional Features Section */}
            <label className='h1-regular mr-5'>Additional Features</label>
            <div className='mx-2 '>
                <FormControlLabel
                    control={<Checkbox
                        defaultChecked={false}
                        checked={state.selectAll}
                        classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                        onChange={(e) => handleSelectAll(e?.target.checked)}
                    />}
                    label={"Select All"} />
            </div>

            <Grid container>
                {state.features.length === 0 ? (
                    Array.from({ length: 24 }).map((_, idx) => (
                        <Grid md={3} key={idx} className='d-flex mx-2'>
                            <Skeleton variant="rectangular" width={24} height={24} className="mr-2" />
                            <Skeleton variant="text" width={100} height={32} />
                        </Grid>
                    ))
                ) : (
                    state.features.filter(x => x.FeatureType === 1).map(item =>
                        <Grid md={3} key={item.Id} className='d-flex mx-2'>
                            <FormControlLabel
                                control={<Checkbox
                                    defaultChecked={false}
                                    checked={item.SelectedFeature}
                                    classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                                    onChange={(e) => handleAdditionalFeature(item.Id, e?.target.checked)}
                                />}
                                label={item.Name} />
                        </Grid>
                    )
                )}
            </Grid>

            <div className='d-flex mt-3  justify-flex-end'>
                <ButtonComponent
                    ButtonLabel={state.buttonLable}
                    ClassName={"mr-2"}
                    Variant={'primary-btn'}
                    OnClick={handleCreatePlan}
                    Loading={isLoading}
                />
                <ButtonComponent
                    ButtonLabel='Cancel'
                    ClassName={""}
                    Variant={'secondary-btn'}
                    OnClick={() => { navigate(-1) }}
                />
            </div>
        </Box>
    )
}

export default CreateEditPlan